import { formatDistanceToNow } from "date-fns";

export type DateFormatPreset =
  | "date" // 2026-02-10
  | "time" // 14:30
  | "datetime" // 2026-02-10 14:30
  | "short-date" // Feb 10
  | "long-date" // 2026 Feb 10
  | "long-datetime" // 2026 Feb 10, 14:30
  | "relative"; // 2 hours ago

export type DateInput = string | number | Date | null | undefined;

export interface FormatDateOptions {
  timezone?: string; // IANA timezone e.g. "America/Toronto"
  locale?: string; // BCP 47 locale e.g. "en-CA"
}

function toParts(
  date: Date,
  locale: string,
  timezone: string | undefined,
  intlOptions: Intl.DateTimeFormatOptions
): Record<string, string> {
  const fmt = new Intl.DateTimeFormat(locale, {
    ...intlOptions,
    ...(timezone ? { timeZone: timezone } : {}),
  });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(date)) {
    parts[p.type] = p.value;
  }
  return parts;
}

export function formatDate(
  input: DateInput,
  preset: DateFormatPreset = "date",
  options: FormatDateOptions = {}
): string {
  if (input == null) return "\u2014";

  const date = input instanceof Date ? input : new Date(input);
  if (isNaN(date.getTime())) return "\u2014";

  if (preset === "relative") {
    return formatDistanceToNow(date, { addSuffix: true });
  }

  const locale = options.locale || "en-CA";
  const tz = options.timezone;

  switch (preset) {
    case "date": {
      // 2026-02-10
      const p = toParts(date, locale, tz, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hourCycle: "h23",
      });
      return `${p.year}-${p.month}-${p.day}`;
    }
    case "time": {
      // 14:30
      const p = toParts(date, locale, tz, {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      });
      return `${p.hour}:${p.minute}`;
    }
    case "datetime": {
      // 2026-02-10 14:30
      const p = toParts(date, locale, tz, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      });
      return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
    }
    case "short-date": {
      // Feb 10
      const p = toParts(date, locale, tz, {
        month: "short",
        day: "numeric",
      });
      return `${p.month} ${p.day}`;
    }
    case "long-date": {
      // 2026 Feb 10
      const p = toParts(date, locale, tz, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      return `${p.year} ${p.month} ${p.day}`;
    }
    case "long-datetime": {
      // 2026 Feb 10, 14:30
      const p = toParts(date, locale, tz, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      });
      return `${p.year} ${p.month} ${p.day}, ${p.hour}:${p.minute}`;
    }
    default:
      return date.toISOString();
  }
}
